<script setup>
import { formatMarketName, toFixedTwo, safeTranslate, formatOdd } from '@/helpers';
import { useAuthStore } from '@/stores/auth';
import { useBetslipStore } from '@/stores/betslip';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUsersStore } from '@/stores/users';
import { useResponseMessageStore } from '@/stores/response_message'; 
import { useFixturesStore } from '@/stores/fixtures';
import DefaultButton from './Buttons/DefaultButton.vue';
import Spinner from './Spinner.vue';
import router from '@/router';

const { t, te } = useI18n()
const auth = useAuthStore()
const betslip = useBetslipStore()
const fixtures = useFixturesStore()
const show = ref(false)
const users = useUsersStore()
const responseMessageStore = useResponseMessageStore()

const { getCredits, getUser, isAuth } = storeToRefs(auth)
const { getSlips, selections, totalOdd, minAndMax, cols, hasChanges, hasUpdates, getSendingTicket, bonusValue, getOddType} = storeToRefs(betslip)
const { getMarket, getEvent} = storeToRefs(fixtures)
const { getUsers } = storeToRefs(users)
const autoAcceptChanges = ref(true)
const amountInput = ref()
const amount = ref()
const currency = ref(getCredits.value[0]?.cuid._id)
const selectedPlayer = ref('')

const validateAmount = (e) => {
    let value = e.target.value.replace(',', '.');
    amount.value = value.replace(/[^0-9.]/g, '') // Allow only numbers and dot
        .replace(/(\..*?)\..*/g, '$1') // Allow only one dot
        .replace(/(\.\d{2})\d*/g, '$1'); // Limit to 2 decimal places
}

const blockInvalidCharsOnAmount = (event) => {
    let value = event.target.value.replace(',', '.')
    const validChars = /^[0-9.,]$/; // Allow digits and dot
    if (!validChars.test(event.key)) {
        event.preventDefault(); // Block invalid characters
    }

    const [integerPart, decimalPart] = value.split('.');
    if (decimalPart && decimalPart.length >= 2) {
        event.preventDefault()
    }
}
const event = (eid) => {
    let event =  getEvent.value(eid)
    if(!event){
        betslip.pullSlip(eid)
    }
    return event
}
const minAmount = computed(() => {
    let [min] = minAndMax.value
    return min * (amount.value/cols.value)
})
const maxAmount = computed(() => {
    let [min, max] = minAndMax.value
    return max * (amount.value/cols.value)
})
const toReturn = computed(() => {
    return totalOdd.value * amount.value
})
const bonusToReturn = computed(() => {
    let bon = 0
    if(!isSystem.value && maxAmount.value){
        bon =  (bonusValue.value / 100) * maxAmount.value
    }else if(isSystem.value && minAmount.value){
        bon =  (bonusValue.value / 100) * minAmount.value
    }
    return bon
})
const isSystem = computed(() => {
    let obj = {}
    for (let eid in getSlips.value) {
        if (!(eid in obj)) obj[eid] = {}
        for (let i in getSlips.value[eid].betslips) {
            if (!(i in obj[eid])) {
                obj[eid][i] = getMarket.value(getSlips.value[eid].betslips[i].mi)?.sysnr
            }
            if ((new Set(Object.values(obj[eid]))).size !== Object.values(obj[eid]).length) {
                return true
            }
        }
    }

    return false
})
const marketName = (selection, event) => {
  const market = getMarket.value(selection.mi)
  if (!market) return ''
  const fullMarket = { ...market, nrs: selection.mnrs || market.nrs }

  return formatMarketName(fullMarket, event)
}


const selectionTranslation = (selection) => { 
    return safeTranslate('selections', selection.n, t, te)
}

const fetchUsers = async () => {
    await users.fetchUsers()
}
const placeBet = () => {
    if (isAuth.value) {
        if (hasUpdates.value && !autoAcceptChanges.value) {
            betslip.acceptUpdates()
        } else {
            const currencyId = currency.value
            const betAmount = amount.value
            const childId = getUser.value?.rl == 'agt' && selectedPlayer.value ? selectedPlayer.value : null
            betslip.send(currencyId, betAmount, childId)
        }
    }else {
        router.push({ name: 'login' })
    }
}
watch(selections, (value) => {
    if (!value) {
        amount.value = ''
        if (amountInput.value) {
            amountInput.value.value = ''
            selectedPlayer.value=''
        }
    }
})
onMounted(() => {
    window.addEventListener('beforeunload', handleBeforeUnload)
    betslip.initializeFromLocalStorage()

    const navType = performance.getEntriesByType('navigation')[0]?.type || 'navigate'
    const ticketIdAtReload = localStorage.getItem('ticketIdAtReload')

    if (navType === 'reload' && ticketIdAtReload) {
        betslip.rejectChanges()
        responseMessageStore.setMessage(t('betslips.refuseOfferOnPageReload'))
        localStorage.removeItem('ticketIdAtReload')
    }

    betslip.bindSocketEvents()
    if (getUser.value?.rl == 'agt') {
        fetchUsers()
    }
})
const handleBeforeUnload = (e) => {
    if (hasChanges.value) {
        e.preventDefault()
        e.returnValue = ''
        return ''
    }
}
onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload)
})
const acceptIfNeeded = () => {
  if (autoAcceptChanges.value && (hasUpdates.value || hasChanges.value)) {
    betslip.acceptUpdates()
  }
}
watch([hasUpdates, hasChanges], acceptIfNeeded)
watch(autoAcceptChanges, acceptIfNeeded)
</script>

<template>
    <Spinner :show="getSendingTicket" />
    <div 
        v-if="getSendingTicket"
        class="fixed inset-0 bg-black bg-opacity-50 z-50"
    ></div>
    <div class="fixed bottom-0 left-0 right-0 pb-16 max-h-[95vh] overflow-hidden z-40" v-if="selections">
        <div class="flex justify-center">
            <div
                class="max-w-sm w-full rounded-xl overflow-hidden"
                :class="[show ? 'bg-[var(--color-whiteF0)]' : 'bg-white']"
            >
                <div
                    class="py-2 flex items-center justify-between"
                    :class="{'border-b': show}"
                    @click="show = !show"
                >
                    <div class="flex-1 flex justify-between items-center px-2">
                        <div class="flex items-center">
                            <div class="w-5 h-5 bg-[var(--color-primary1)] flex items-center justify-center rounded-full text-sm text-white font-bold">
                                {{ selections }}
                            </div>
                            <p class="text-[15px] font-extrabold text-[var(--color-grey4)] ml-2">{{$t('general.9')}}</p>
                        </div>

                        <div v-if="getCredits.length > 1">
                            <select @click.stop v-model="currency" class="border-x-0 border-t-0 border-neutral-300 appearance-none text-xs bg-transparent focus:ring-0">
                                <option v-for="c in getCredits" :value="c.cuid._id">
                                    {{ c.cuid.cod }}
                                </option>
                            </select>
                        </div>

                        <div class="ml-auto text-sm font-medium">
                            <template v-if="!isSystem">
                                {{ formatOdd({p: totalOdd}, getOddType) }}
                            </template>
                            <template v-else>
                                {{ formatOdd({p: minAndMax[0]}, getOddType) }} - {{ formatOdd({p: minAndMax[1]}, getOddType) }}
                            </template>
                        </div>
                    </div>

                    <div class="px-2 py-1.5 border-l">
                        <div class="bet-ShowHide" :class="{'opened': show}" />
                    </div>
                </div>

                <!-- START:Content -->
                 <div v-show="show">
                    <div class="p-1.5">
                        <div class="bg-white rounded py-3 px-0.5 max-h-[55vh] overflow-y-auto">
                            <ul class="space-y-4">
                                <li v-for="slip,eid in getSlips">
                                    <div class="flex-1 flex">
                                        <div class="px-2">
                                            <button class="bs-XMark" @click="betslip.pullSlip(eid)" />
                                        </div>
                                        <div class="flex-1">
                                            <h3 class="text-[13px] font-extrabold text-[var(--color-primary1)]">
                                                <span v-if="event(eid)?.o == 'false'">
                                                    {{ event(eid)?.h }} v {{ event(eid)?.a }}
                                                </span>
                                               <span v-else> 
                                                    {{ event(eid)?.outrightName }}
                                               </span>
                                            </h3>

                                            <ul class="flex-1">
                                                <li v-for="selection in slip.betslips">
                                                    <div class="flex justify-between flex-1" :class="{'line-through': (selection.st == 3 || selection.st == 2) }">
                                                        <p
                                                            class="text-xs font-bold text-[var(--color-grey44)]"
                                                        >
                                                           <span>{{ marketName(selection, event(eid)) }}</span>
                                                            ({{ selectionTranslation(selection) }})
                                                        </p>
                                                        <p
                                                            class="text-xs pr-2 font-bold"
                                                            :class="{
                                                                'text-red-600': selection.mv == -1,
                                                                'text-emerald-500': selection.mv == 1,
                                                                'text-[var(--color-grey44)]': !selection.mv
                                                            }"
                                                        >{{ formatOdd(selection, getOddType) }}</p>
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </li>
                            </ul>
                        </div>

                        <div class="mt-1.5 flex justify-between items-center">
                            <button class="py-1.5 px-3 text-center text-red-600 font-bold text-[13px]" @click="betslip.clear">
                                {{$t('general.22')}}
                            </button>
                
                            <select
                               v-if="getUser && getUser.rl == 'agt'"
                                class="appearance-none bg-transparent text-[13px] text-[var(--color-grey4)] border-0 focus:ring-0"
                                v-model="selectedPlayer"
                            >
                                <option value="" disabled selected>{{$t('general.23')}}</option>
                                <option
                                    v-for="user in getUsers"
                                    :value="user._id"
                                >
                                {{ user.un }}
                                </option>
                            </select>
                     
                            <span class="text-xs px-3" v-if="bonusValue">{{$t('general.24')}}({{bonusValue}}%)= {{toFixedTwo(bonusToReturn)}}</span>
                        </div>
                    </div>
                 </div>
                <div class="px-4 py-2 flex items-center gap-2 border-t-2">
                    <label for="acceptToggle" class="flex items-center cursor-pointer  justify-between w-full">
                        <span class="text-xs font-bold text-[var(--color-grey44)]">{{$t('general.21')}}</span>
                        <div class="relative">
                        <input 
                            type="checkbox" 
                            v-model="autoAcceptChanges" 
                            id="acceptToggle" 
                            class="sr-only peer"
                        />
                        <div 
                            class="w-11 h-6 bg-gray-300 rounded-full transition-colors peer-checked:bg-[var(--color-primary2)]"
                        ></div>
                        <div 
                            class="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transform transition-transform peer-checked:translate-x-5"
                        ></div>
                        </div>
                    </label>
                </div>
                <div class="flex">
                    <input
                        ref="amountInput"
                        type="text"
                        inputmode="decimal"
                        lang="en"
                        pattern="[0-9.]*"
                        class="w-1/2 bg-[#222] text-[17px] text-[var(--color-primary3)] placeholder:text-[var(--color-primary3)] border-0 font-extrabold focus:ring-0"
                        :placeholder="$t('general.15')"
                        @input="validateAmount"
                        @keypress="blockInvalidCharsOnAmount"
                        v-if="!hasChanges && !hasUpdates"
                    >
                    <button
                        class="h-12 w-1/2 text-center bg-gradient-to-br from-red-600 to-red-400 text-white text-base font-extrabold"
                        @click="betslip.rejectChanges"
                        v-if="hasChanges && !hasUpdates"
                    >
                        {{$t('general.25')}}
                    </button>
                    <DefaultButton
                        class="bet-btn text-center bg-placebet text-base"
                        :class="{'w-1/2': !hasUpdates, 'w-full': !hasChanges && hasUpdates}"
                        @click="placeBet"
                        :disabled="getSendingTicket"   
                    >
                        <template v-if="hasChanges || hasUpdates">
                            {{$t('general.16')}} 
                        </template>
                        <template v-else >
                            <div class="flex flex-col">
                                {{$t('general.10')}}
                                <p v-if="amount" class="text-[11px] font-bold text-[var(--color-grey4)] leading-none">
                                    <template v-if="!isSystem">
                                        {{$t('general.11')}} {{ toFixedTwo(toReturn) }}
                                    </template>
                                    <template v-else>
                                        {{$t('general.12')}} {{ toFixedTwo(minAmount) }} ---> {{$t('general.13')}} {{ toFixedTwo(maxAmount) }}
                                    </template>
                                </p>
                            </div>
                        </template>
                    </DefaultButton>
                 </div>
                 <!-- END:Content -->
            </div>
        </div>
    </div>
</template>

<style scoped>
.bet-ShowHide {
    @apply block w-6 h-6 cursor-pointer rotate-180;
    mask-image: url('/chevron-down.svg');
    background-color: #000;
}
.bet-ShowHide.opened {
    @apply rotate-0;
}

.bs-XMark {
    background-image: url('/x-mark.svg');
    width: 14px;
    height: 14px
}
.bet-btn{
    height: 48px; 
    font-weight: 800; 
    font-size: 1rem;
}
.toggle {
  position: relative;
  width: 44px;
  height: 22px;
  background: #ccc;
  border-radius: 9999px;
  cursor: pointer;
  transition: background 0.3s;
}
.toggle input {
  display: none;
}
.toggle-circle {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.3s;
}
.toggle input:checked + .toggle-bg {
    background: var(--color-primary2);
}
.toggle input:checked ~ .toggle-circle {
  transform: translateX(22px);
}
</style>